import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  Download,
  Trash2,
  Eye,
  Loader2,
  ArrowRight
} from 'lucide-react';

export default function ReconciliationPage() {
  const { api } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [matching, setMatching] = useState(false);
  
  // Upload form state
  const [file, setFile] = useState(null);
  const [bankName, setBankName] = useState('');

  const fetchBatches = useCallback(async () => {
    try {
      const response = await api.get('/reconciliation/batches');
      setBatches(response.data.batches || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
    } else {
      toast.error('Please select a valid CSV file');
      e.target.value = '';
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!file || !bankName) {
      toast.error('Please select a file and enter bank name');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/reconciliation/upload?bank_name=${encodeURIComponent(bankName)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Uploaded ${response.data.total_entries} entries from ${file.name}`);
      setFile(null);
      setBankName('');
      // Reset file input
      document.getElementById('csv-file').value = '';
      fetchBatches();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload file';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleAutoMatch = async (batchId) => {
    setMatching(true);
    try {
      const response = await api.post(`/reconciliation/batch/${batchId}/auto-match`);
      toast.success(`Matched ${response.data.matched} of ${response.data.total_entries} entries (${response.data.match_rate})`);
      fetchBatches();
      if (selectedBatch === batchId) {
        fetchBatchDetails(batchId);
      }
    } catch (error) {
      toast.error('Failed to run auto-match');
    } finally {
      setMatching(false);
    }
  };

  const fetchBatchDetails = async (batchId) => {
    setLoadingDetails(true);
    setSelectedBatch(batchId);
    try {
      const response = await api.get(`/reconciliation/batch/${batchId}`);
      setBatchDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch batch details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;
    
    try {
      await api.delete(`/reconciliation/batch/${batchId}`);
      toast.success('Batch deleted');
      setBatches(batches.filter(b => b.id !== batchId));
      if (selectedBatch === batchId) {
        setSelectedBatch(null);
        setBatchDetails(null);
      }
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'secondary', icon: AlertTriangle, label: 'Pending Match' },
      partial: { variant: 'warning', icon: AlertTriangle, label: 'Partial Match' },
      matched: { variant: 'success', icon: CheckCircle, label: 'Fully Matched' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold">Bank Reconciliation</h1>
        <p className="text-muted-foreground">Upload bank statements and match with platform transactions</p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload Bank Statement
          </CardTitle>
          <CardDescription>
            Upload a CSV file with columns: date, reference_id, amount, type (credit/debit), description
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="bank-name">Bank Name</Label>
              <Input
                id="bank-name"
                placeholder="e.g., HDFC Bank"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="csv-file">CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mt-1"
              />
            </div>
            <Button type="submit" disabled={uploading || !file || !bankName} data-testid="upload-btn">
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Batches List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Reconciliation Batches
              </span>
              <Button variant="ghost" size="icon" onClick={fetchBatches} data-testid="refresh-batches-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : batches.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No reconciliation batches yet</p>
                <p className="text-sm">Upload a bank statement to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {batches.map((batch) => (
                  <div
                    key={batch.id}
                    className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                      selectedBatch === batch.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => fetchBatchDetails(batch.id)}
                    data-testid={`batch-${batch.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{batch.bank_name}</p>
                        <p className="text-sm text-muted-foreground">{batch.filename}</p>
                      </div>
                      {getStatusBadge(batch.status)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {batch.matched_entries}/{batch.total_entries} matched
                      </span>
                      <span className="text-muted-foreground">
                        {formatDate(batch.uploaded_at)}
                      </span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAutoMatch(batch.id);
                        }}
                        disabled={matching}
                        data-testid={`auto-match-${batch.id}`}
                      >
                        {matching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        <span className="ml-1">Auto-Match</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBatch(batch.id);
                        }}
                        data-testid={`delete-batch-${batch.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Batch Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Batch Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedBatch ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>Select a batch to view details</p>
              </div>
            ) : loadingDetails ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : batchDetails ? (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-card border">
                    <p className="text-sm text-muted-foreground">Total Credits</p>
                    <p className="text-xl font-bold text-green-500">
                      {formatCurrency(batchDetails.total_credits)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-card border">
                    <p className="text-sm text-muted-foreground">Total Debits</p>
                    <p className="text-xl font-bold text-red-500">
                      {formatCurrency(batchDetails.total_debits)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-card border">
                    <p className="text-sm text-muted-foreground">Matched</p>
                    <p className="text-xl font-bold text-primary">
                      {batchDetails.matched_entries}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-card border">
                    <p className="text-sm text-muted-foreground">Unmatched</p>
                    <p className="text-xl font-bold text-yellow-500">
                      {batchDetails.unmatched_entries}
                    </p>
                  </div>
                </div>

                {/* Entries List */}
                <div>
                  <p className="text-sm font-medium mb-2">Entries ({batchDetails.entries?.length || 0})</p>
                  <div className="max-h-[400px] overflow-y-auto space-y-2">
                    {batchDetails.entries?.map((entry) => (
                      <div
                        key={entry.id}
                        className={`p-3 rounded-lg border text-sm ${
                          entry.matched 
                            ? 'border-green-500/30 bg-green-500/5' 
                            : 'border-yellow-500/30 bg-yellow-500/5'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-xs">{entry.reference_id}</span>
                          {entry.matched ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-yellow-500" />
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={entry.type === 'credit' ? 'text-green-500' : 'text-red-500'}>
                            {entry.type === 'credit' ? '+' : '-'}{formatCurrency(entry.amount)}
                          </span>
                          <span className="text-muted-foreground">{entry.date}</span>
                        </div>
                        {entry.matched && entry.matched_transaction_id && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Matched: {entry.matched_transaction_id.substring(0, 8)}...
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
